// Phase 7: gated readiness report (email gate + PDF/deal room delivery) not built; copy must not promise it until it ships.
import React, { useReducer, useState, useEffect } from 'react';
import { usePageMeta } from '../lib/usePageMeta';
import Nav from '../components/Nav';
import Footer from '../components/Footer';
import QuizQuestion from '../components/QuizQuestion';
import DiagnosticResult from '../components/DiagnosticResult';
import InterestFormModal from '../components/InterestFormModal';
import { QUIZ_QUESTIONS, DIMENSION_ORDER } from '../data/quizQuestions';
import { quizReducer, initialQuizState, scoreDimensions, readinessBand, recommendMode, QuizState } from '../lib/quizState';

const SESSION_KEY = 'parsant.quiz.state.v1';

function loadState(): QuizState {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return initialQuizState();
    const parsed = JSON.parse(raw);
    if (typeof parsed.currentIndex === 'number' && typeof parsed.answers === 'object') {
      return parsed;
    }
  } catch {}
  return initialQuizState();
}

const Quiz: React.FC = () => {
  const [state, dispatch] = useReducer(quizReducer, undefined, loadState);
  const [isFormOpen, setIsFormOpen] = useState(false);
  usePageMeta(
    'AI Readiness Assessment | Parsant',
    'Ten questions, about two minutes, and a free readiness report on where your revenue data holds up and where it breaks.'
  );

  useEffect(() => {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(state));
  }, [state]);

  const isComplete = Object.keys(state.answers).length === QUIZ_QUESTIONS.length;
  const currentQ = QUIZ_QUESTIONS[state.currentIndex];

  const scores = scoreDimensions(state.answers);
  const total = DIMENSION_ORDER.reduce((sum, d) => sum + scores[d], 0);
  const band = readinessBand(total);
  const recommendation = recommendMode(scores, state.answers['scope-1']);

  return (
    <main className="bg-void-black min-h-screen">
      <Nav onContactClick={() => setIsFormOpen(true)} />

      <section className="pt-32 pb-16">
        <div className="container mx-auto max-w-7xl px-6">
          {!isComplete && (
            <>
              <div className="mb-12">
                <div className="eyebrow eyebrow-accent mb-4">Ten questions · About two minutes</div>
                <h1 className="text-4xl md:text-5xl font-bold text-signal-white mb-4 tracking-tight">
                  AI Readiness Assessment
                </h1>
                <p className="text-lg text-steel measure">
                  Ten questions take about two minutes. We give you a candid read, including
                  "not yet" or "not a Parsant fit" when that is the honest answer.
                </p>
              </div>
              {currentQ && (
                <QuizQuestion
                  question={currentQ}
                  index={state.currentIndex}
                  total={QUIZ_QUESTIONS.length}
                  canBack={state.currentIndex > 0}
                  onAnswer={(answerIndex) =>
                    dispatch({ type: 'ANSWER', questionId: currentQ.id, answerIndex })
                  }
                  onBack={() => dispatch({ type: 'BACK' })}
                />
              )}
            </>
          )}

          {isComplete && (
            <DiagnosticResult
              scores={scores}
              band={band}
              recommendation={recommendation}
              answers={state.answers}
              onReset={() => {
                sessionStorage.removeItem(SESSION_KEY);
                dispatch({ type: 'RESET' });
              }}
              onContactClick={() => setIsFormOpen(true)}
            />
          )}
        </div>
      </section>

      <Footer />
      <InterestFormModal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} />
    </main>
  );
};

export default Quiz;
